import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  JSX,
} from 'react';

import * as Sentry from '@sentry/react';

import { useRevenueCat } from '@hooks/revcat/useRevenueCat';
import { usePaywall } from '@hooks/revcat/usePaywall';

import Paywall from '@components/Paywall/Paywall';
import PaywallCta from '@components/Paywall/PaywallCta';
import PaywallLock from '@components/Paywall/PaywallLock';

import {
  PaywallPageContext,
  PaywallPrices,
  ActiveSubscription,
  ViewerEntitlementStatus,
  PaywallCtaProps,
  PaywallLockProps,
  SubscriptionOffering,
} from '../components/Paywall/paywall.types';

import { TrayProvider, useTray } from './TrayContext';
import SHDTray from '@ui/Tray/SHDTray';

interface PaywallContextInterface {
  prices: PaywallPrices;
  activeSubscription: ActiveSubscription | undefined;
  isEntitled: boolean;
  entitlement: ViewerEntitlementStatus;
  isPaywallActive?: boolean;
  packageName?: string;
  handlePaywallOpen: () => void;
  handlePaywallClose: () => void;
  renderCTA: (props: PaywallCtaProps) => JSX.Element;
  renderPaywallLock: (props: PaywallLockProps) => JSX.Element;
  setHandleTrayCTA: (value: boolean) => void;
  setEntitlement: (status: ViewerEntitlementStatus) => void;
  setTrackingContext: (context: PaywallPageContext) => void;
  setRedirectUrl: (url: string | undefined) => void;
  setTarget: (target: string | undefined) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderLockOrCTA: (statusProps: any) => JSX.Element;
}

export const PaywallProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { fetchPrices, fetchCustomerInfo, packageName } = useRevenueCat();
  const [activeSubscription, setActiveSubscription] = useState<
    ActiveSubscription | undefined
  >(undefined);
  const [isEntitled, setIsEntitled] = useState<boolean>(false);
  const [handleTrayCTA, setHandleTrayCTA] = useState<boolean>(false);
  const [trackingContext, setTrackingContext] = useState<PaywallPageContext>();
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [target, setTarget] = useState<string | undefined>(undefined);

  const [entitlement, setEntitlement] = useState<
    ViewerEntitlementStatus | undefined
  >();
  const [prices, setPrices] = useState<PaywallPrices>({
    monthly: '$5.99',
    annual: '$35.99',
    slhdOfferings: null,
  });

  const {
    isPaywallActive,
    handlePaywallOpen,
    handlePaywallClose,
    handleUnlockAccess,
  } = usePaywall({ prices, context: trackingContext });

  const { hideTray } = useTray();

  useEffect(() => {
    const fetchRCPrices = async () =>
      await fetchPrices().then((currentPrices: PaywallPrices | undefined) => {
        if (currentPrices) {
          setPrices({ ...currentPrices });
        }
      });
    fetchRCPrices();
  }, [fetchPrices, entitlement]);

  interface CustomerInfo {
    isEntitled?: boolean;
    activeSubscription?: ActiveSubscription;
  }

  useEffect(() => {
    const fetchRcCustomerInfo = async (displayPrices: PaywallPrices) =>
      await fetchCustomerInfo(displayPrices)
        .then((customer: CustomerInfo) => {
          setActiveSubscription(customer?.activeSubscription);
          setIsEntitled(customer?.isEntitled || false);
        })
        .catch((error) => {
          Sentry.captureException(error, {
            extra: {
              error,
              message: 'Error fetching RevenueCat customer info',
            },
          });
        });
    fetchRcCustomerInfo(prices);
  }, [fetchCustomerInfo, prices, entitlement]);

  const renderCTA = useCallback(
    (props: PaywallCtaProps) => {
      if (props.context) {
        setTrackingContext(props.context);
      }
      return (
        <PaywallCta
          {...props}
          onPaywallOpen={handlePaywallOpen}
          prices={prices}
          context={trackingContext}
        />
      );
    },
    [prices, handlePaywallOpen, trackingContext]
  );

  const renderTrayCTA = useCallback(
    (props: PaywallCtaProps) => {
      return (
        <TrayProvider>
          <SHDTray
            onClose={() => {
              hideTray();
              setHandleTrayCTA(false);
            }}
            inlineClose
          >
            {renderCTA({
              ...props,
            })}
          </SHDTray>
        </TrayProvider>
      );
    },
    [hideTray, renderCTA]
  );

  const renderPaywallLock = useCallback((props: PaywallLockProps) => {
    if (props.context) {
      setTrackingContext(props.context);
    }
    return <PaywallLock {...props} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLockOrCTA = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (statusProps: any) => {
      if (statusProps.context) {
        setTrackingContext(statusProps.context);
      }
      switch (entitlement) {
        case ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN:
          if (statusProps.triggerPaywall) {
            handlePaywallOpen();
          } else {
            return renderCTA({
              ...statusProps,
            });
          }
          break;
        case ViewerEntitlementStatus.ENTITLED_BUT_PRIVATE:
        case ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT:
          return renderPaywallLock({
            ...statusProps,
          });
          break;
        default:
          break;
      }
    },
    [entitlement, handlePaywallOpen, renderCTA, renderPaywallLock]
  );

  return (
    <PaywallContext.Provider
      value={{
        prices,
        activeSubscription,
        isEntitled,
        entitlement,
        isPaywallActive,
        packageName,
        setEntitlement,
        handlePaywallOpen,
        handlePaywallClose,
        renderCTA: renderCTA,
        setHandleTrayCTA,
        renderPaywallLock: renderPaywallLock,
        renderLockOrCTA: renderLockOrCTA,
        setTrackingContext,
        setRedirectUrl,
        setTarget,
      }}
    >
      {children}
      <Paywall
        prices={prices}
        context={trackingContext}
        isOpen={isPaywallActive}
        onClose={() => handlePaywallClose()}
        onUnlockAccess={(term: SubscriptionOffering) =>
          handleUnlockAccess(term)
        }
      />
      {handleTrayCTA && (
        <>
          {renderTrayCTA({
            isClaimed: false,
            isPrivate: false,
            isTray: true,
          })}
        </>
      )}
    </PaywallContext.Provider>
  );
};

const PaywallContext = createContext<PaywallContextInterface | undefined>(
  undefined
);

interface PaywallContextProps {
  entitlementStatus: ViewerEntitlementStatus;
  trackingContext?: PaywallPageContext;
  target?: string | undefined;
  redirectUrl?: string | undefined;
}

export const usePaywallContext = ({
  entitlementStatus = ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT,
  trackingContext,
  target,
  redirectUrl,
}: PaywallContextProps) => {
  const context = useContext(PaywallContext);
  if (entitlementStatus !== undefined && context?.entitlement === undefined) {
    context?.setEntitlement(entitlementStatus);
  }

  if (trackingContext !== undefined) {
    context?.setTrackingContext(trackingContext);
  }

  if (redirectUrl !== undefined) {
    context?.setRedirectUrl(redirectUrl);
  }

  if (target !== undefined) {
    context?.setTarget(target);
  }

  if (context === undefined) {
    throw new Error('usePaywallContext must be used within a PaywallProvider');
  }
  return context;
};

export default PaywallContext;
